import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api'; // configured axios instance with auth
import AdminNav from '../../components/admin/AdminNav';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../components/common/Pagination';
import ResultCount from '../../components/common/ResultCount';
import { paginate } from '../../utils/paginate';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/useIsMobile';

export default function PendingAdminApprovals() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [expandedCardId, setExpandedCardId] = useState(null); // mobile card currently expanded
  // Read by the load effect, which must not re-run when the language changes
  const tRef = useRef(t);
  tRef.current = t;
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [page, setPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierFor, setSelectedSupplierFor] = useState({}); // map userId -> supplierId
  const [loadingApprovals, setLoadingApprovals] = useState({}); // map userId -> bool
  const [rejectTarget, setRejectTarget] = useState(null); // user whose rejection reason is being entered
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, suppliersRes] = await Promise.all([
          api.get('/api/users', { params: { role: 'pending' } }),
          api.get('/api/suppliers'), // needed to assign supplier-admins
        ]);

        // Only users who registered with "Register as Admin" (role 'pending')
        let pending = Array.isArray(usersRes.data)
          ? usersRes.data
          : Array.isArray(usersRes.data.users)
          ? usersRes.data.users
          : [];

        pending = pending.filter((u) => u.role === 'pending');

        setPendingAdmins(pending);
        setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
      } catch (err) {
        console.error('Fetch error:', err);
        toast.error(tRef.current('adminPending.loadFailed'));
      }
    };
    load();
  }, []);

  // Tells the navigation badge that the pending count changed
  const notifyCountChanged = () => window.dispatchEvent(new Event('pending-admins-changed'));

  // The server reports whether the notification email was delivered
  const showEmailResult = (res, sentText) => {
    if (res.data?.emailSent) toast.info(sentText);
    else toast.warning(t('adminPending.emailFailed'));
  };

  const approveAdmin = async (id) => {
    const user = pendingAdmins.find((u) => u._id === id);
    const supplierId = selectedSupplierFor[id];
    if (!supplierId) {
      toast.error(t('adminPending.noSupplier'));
      return;
    }

    setLoadingApprovals((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.post(`/api/users/approve-supplier-admin/${id}`, {
        supplierId,
      });
      if (res.status === 200) {
        setPendingAdmins((prev) => prev.filter((u) => u._id !== id));
        toast.success(
          user
            ? t('adminPending.approvedFor', { name: user.username })
            : t('adminPending.approvedGeneric')
        );
        showEmailResult(res, t('adminPending.approveEmail'));
        notifyCountChanged();
      } else {
        toast.error(t('adminPending.approveFailed'));
      }
    } catch (err) {
      console.error('Error approving admin:', err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t('adminPending.approveFailed'));
      }
    } finally {
      setLoadingApprovals((prev) => ({ ...prev, [id]: false }));
    }
  };

  const rejectAdmin = async (id, reason) => {
    const user = pendingAdmins.find((u) => u._id === id);
    setLoadingApprovals((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.put(`/api/users/${id}/reject`, { reason });
      if (res.status === 200) {
        setPendingAdmins((prev) => prev.filter((u) => u._id !== id));
        toast.success(
          user
            ? t('adminPending.rejectedFor', { name: user.username })
            : t('adminPending.rejectedGeneric')
        );
        showEmailResult(res, t('adminPending.rejectEmail'));
        notifyCountChanged();
        setRejectTarget(null);
      } else {
        toast.error(t('adminPending.rejectFailed'));
      }
    } catch (err) {
      console.error('Error rejecting admin:', err);
      toast.error(t('adminPending.rejectFailed'));
    } finally {
      setLoadingApprovals((prev) => ({ ...prev, [id]: false }));
    }
  };

  const { pageItems, page: currentPage, pageCount } = paginate(pendingAdmins, page);

  // Compact expandable card for mobile; same handlers and controls as the desktop row
  const renderMobileCard = (user) => {
    const isOpen = expandedCardId === user._id;
    const busy = loadingApprovals[user._id];
    const requested = user.requestedSupplierName;
    return (
      <div key={user._id} className="min-w-0 border rounded-md bg-white shadow-sm p-3 mb-3 text-sm">
        <div className="flex items-start gap-2 min-w-0">
          <div className="min-w-0 flex-1">
            <p className="font-semibold truncate" title={user.username}>{user.username}</p>
            <p className="text-gray-600 truncate" title={user.email}>{user.email}</p>
          </div>
          <button
            type="button"
            onClick={() => setExpandedCardId(isOpen ? null : user._id)}
            aria-expanded={isOpen}
            aria-label={t('adminUsers.toggleDetails')}
            className="shrink-0 p-1 text-blue-600 focus:outline-none"
          >
            <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
          </button>
        </div>
        <div className="mt-2 min-w-0 text-xs text-gray-600">
          {requested && (
            <div className="truncate" title={requested}>
              {t('adminPending.requested')} <span className="font-medium">{requested}</span>
            </div>
          )}
          <div>{t('adminUsers.role_pending')}</div>
        </div>

        {isOpen && (
          <div className="mt-3 border-t border-gray-200 pt-2 space-y-2 min-w-0">
            <label htmlFor={`associate-${user._id}`} className="block text-xs font-medium text-gray-700">
              {t('adminPending.associate')}
            </label>
            <select
              id={`associate-${user._id}`}
              value={selectedSupplierFor[user._id] || ''}
              onChange={(e) => setSelectedSupplierFor((prev) => ({ ...prev, [user._id]: e.target.value }))}
              className="block w-full min-w-0 max-w-full truncate border px-2 py-1 rounded"
            >
              <option value="">{t('adminPending.selectSupplier')}</option>
              {suppliers.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.displayName || s.name}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => approveAdmin(user._id)}
                disabled={busy}
                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              >
                {busy ? t('adminPending.approving') : t('adminPending.approve')}
              </button>
              <button
                onClick={() => { setRejectTarget(user); setRejectReason(''); }}
                disabled={busy}
                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
              >
                {busy ? t('adminPending.rejecting') : t('adminPending.reject')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        <h2 className="text-xl font-bold mb-4">{t('adminPending.title')}</h2>

        {pendingAdmins.length === 0 && <p>{t('adminPending.empty')}</p>}
        <ResultCount total={pendingAdmins.length} page={currentPage} />

        {pageItems.map((user) => isMobile ? renderMobileCard(user) : (
          <div
            key={user._id}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded mb-3 gap-4"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{user.username}</p>
              <p className="text-sm text-gray-600">{user.email}</p>
              {user.requestedSupplierName && (
                <p className="text-sm text-gray-600">
                  {t('adminPending.requested')} <span className="font-medium">{user.requestedSupplierName}</span>
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex flex-col min-w-0 max-w-full">
                <label className="text-sm font-medium mb-1">{t('adminPending.associate')}</label>
                <select
                  value={selectedSupplierFor[user._id] || ''}
                  onChange={(e) =>
                    setSelectedSupplierFor((prev) => ({
                      ...prev,
                      [user._id]: e.target.value,
                    }))
                  }
                  className="border px-2 py-1 rounded max-w-full"
                >
                  <option value="">{t('adminPending.selectSupplier')}</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => approveAdmin(user._id)}
                  disabled={loadingApprovals[user._id]}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loadingApprovals[user._id] ? t('adminPending.approving') : t('adminPending.approve')}
                </button>
                <button
                  onClick={() => { setRejectTarget(user); setRejectReason(''); }}
                  disabled={loadingApprovals[user._id]}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {loadingApprovals[user._id] ? t('adminPending.rejecting') : t('adminPending.reject')}
                </button>
              </div>
            </div>
          </div>
        ))}
        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </div>
      {rejectTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 break-words">{t('adminPending.rejectTitle', { name: rejectTarget.username })}</h2>
            <label htmlFor="reject-reason" className="block text-sm font-medium mb-1">
              {t('adminPending.reason')}
            </label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
              rows={3}
              className="border w-full p-2 rounded mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRejectTarget(null)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                {t('profile.cancel')}
              </button>
              <button
                type="button"
                onClick={() => rejectAdmin(rejectTarget._id, rejectReason.trim())}
                disabled={!rejectReason.trim() || loadingApprovals[rejectTarget._id]}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {t('adminPending.rejectRequest')}
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
    </div>
  );
}
